// ClearAllConfirmDialog — global confirmation modal that asks the
// user to confirm a full reset of their booking enquiry. Mounted
// once in App.js next to <StickySummary>, it listens to the shared
// `clearConfirmOpen` flag in BookingContext, so any surface
// (HomeBookingWidget, StickySummary, or a future button anywhere on
// the site) can trigger it by calling `requestClearAll()` — the
// reset logic itself stays in one place (`confirmClearAll` →
// `clearAll`) inside the provider.

import React, { useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBooking } from "@/contexts/BookingContext";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const COPY = {
  overline: {
    en: "Confirm reset",
    fr: "Confirmer la réinitialisation",
    es: "Confirmar el reinicio",
  },
  title: {
    en: "Clear your entire booking enquiry?",
    fr: "Effacer toute votre demande de réservation ?",
    es: "¿Borrar toda su solicitud de reserva?",
  },
  body: {
    en: "This removes every selected property, room, extra and the contact details you have entered. The floating summary and the Choose Your House grid will also reset. This cannot be undone.",
    fr: "Ceci supprime chaque adresse sélectionnée, chaque chambre, chaque extra ainsi que les coordonnées saisies. Le récapitulatif et la grille « Choisissez votre maison » seront également réinitialisés. Action irréversible.",
    es: "Se eliminarán todas las propiedades, habitaciones y extras seleccionados, así como los datos de contacto introducidos. El resumen flotante y la cuadrícula « Elija su casa » también se restablecerán. Esta acción no se puede deshacer.",
  },
  cancel: { en: "Cancel", fr: "Annuler", es: "Cancelar" },
  yes: { en: "Clear all", fr: "Tout effacer", es: "Borrar todo" },
  done_toast: {
    en: "Your enquiry has been cleared.",
    fr: "Votre demande a été effacée.",
    es: "Su solicitud ha sido borrada.",
  },
};

const ClearAllConfirmDialog = () => {
  const { lang } = useLanguage();
  const { clearConfirmOpen, cancelClearAll, confirmClearAll } = useBooking();

  // ESC key cancels the dialog while it's open.
  useEffect(() => {
    if (!clearConfirmOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") cancelClearAll();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clearConfirmOpen, cancelClearAll]);

  if (!clearConfirmOpen) return null;

  const handleConfirm = () => {
    confirmClearAll();
    toast.success(pick(COPY.done_toast, lang));
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-clear-confirm-title"
      data-testid="booking-clear-confirm"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={pick(COPY.cancel, lang)}
        onClick={cancelClearAll}
        className="absolute inset-0 bg-[#1A1513]/70 backdrop-blur-sm"
        data-testid="booking-clear-confirm-backdrop"
      />
      {/* Card */}
      <div className="relative w-full sm:max-w-[480px] bg-[#FDFBF7] border border-[#D4A373]/60 shadow-2xl mx-4 sm:mx-0">
        <div className="p-6 md:p-7">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#A35133]">
            {pick(COPY.overline, lang)}
          </p>
          <h3
            id="booking-clear-confirm-title"
            className="mt-2 font-serif-x text-2xl md:text-3xl text-[#2C2621] leading-tight"
          >
            {pick(COPY.title, lang)}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-[#5C5248]">
            {pick(COPY.body, lang)}
          </p>
          <div className="mt-6 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button
              type="button"
              onClick={cancelClearAll}
              className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#2C2621] border border-[#2C2621]/25 hover:border-[#2C2621] transition-colors"
              data-testid="booking-clear-confirm-cancel"
              autoFocus
            >
              {pick(COPY.cancel, lang)}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-6 py-3 text-[10px] tracking-[0.3em] uppercase bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="booking-clear-confirm-yes"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              {pick(COPY.yes, lang)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClearAllConfirmDialog;
